/**
 * Menu 3 · Data & Quality — donker analytics-dashboard in vier lagen:
 *
 *   Laag 1  Macro Metrics       (Analytics)  boekingsvolume, workshop-vulgraad, platformscore + leaderboard
 *   Laag 2  Segmentatie-Engine  (Analytics)  volumeverhouding, volume en netto omzet per markt, vergelijkingstabel
 *   Laag 3  3-Assen Review      (Reviews)    Inhoud · Toepasbaarheid|Welzijn · Structuur & lesmateriaal
 *   Laag 4  Feedback Feed       (Feedback)   AI-geschoonde open tekst, per cursus pas vanaf 5 reviews
 *
 * Marktkleuren (blauw/paars/groen) zijn gevalideerd tegen het donkere oppervlak; het
 * blauw–paars-paar zit in de CVD-waarschuwingsband, dus elke grafiek draagt ook directe labels.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Star, ThumbsUp, Sparkles, ShieldCheck, Lock, Ticket, Gauge, Trophy, TrendingUp, TrendingDown, Hourglass } from 'lucide-react';
import {
  getMacro, getDemografie, getReviewAssen, getFeedback,
  Markt, Periode, Assen, As2Variant, PlatformScore, Demografie,
} from '../services/opleiderCockpitApi';
import { eur } from '../components/academy/academyStijl';

export type DataView = 'analytics' | 'reviews' | 'feedback';

const MARKTEN: Markt[] = ['medewerkers', 'professionals', 'bedrijven'];
const MARKT_KLEUR: Record<Markt, string> = { medewerkers: '#3b82f6', professionals: '#c026d3', bedrijven: '#16a34a' };
const PERIODES: Periode[] = ['maand', 'kwartaal', 'jaar'];
const PREFIX = 'opleiderCockpit.data';

const kaart = 'rounded-xl border border-slate-800 bg-slate-900 p-5';
const getal = new Intl.NumberFormat('nl-NL');

const Laad: React.FC = () => (
  <div className="p-10 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400 mx-auto" /></div>
);

const MarktStip: React.FC<{ markt: Markt }> = ({ markt }) => (
  <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: MARKT_KLEUR[markt] }} aria-hidden />
);

const PeriodeKiezer: React.FC<{ waarde: Periode; onChange: (p: Periode) => void }> = ({ waarde, onChange }) => {
  const { t } = useTranslation('pages');
  return (
    <div role="radiogroup" aria-label={t(`${PREFIX}.periode.label`)} className="inline-flex rounded-lg border border-slate-700 bg-slate-900 p-0.5">
      {PERIODES.map((p) => (
        <button
          key={p}
          type="button"
          role="radio"
          aria-checked={waarde === p}
          onClick={() => onChange(p)}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${waarde === p ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}
        >
          {t(`${PREFIX}.periode.${p}`)}
        </button>
      ))}
    </div>
  );
};

// ─── Laag 1: Macro Metrics ───────────────────────────────────────────────────

const KwaliteitsKaart: React.FC<{ k: PlatformScore }> = ({ k }) => {
  const { t } = useTranslation('pages');
  return (
    <div className={kaart}>
      <p className="text-sm text-slate-400 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-400" />{t(`${PREFIX}.macro.kwaliteit`)}</p>
      {k.score != null ? (
        <>
          <p className="mt-2 text-3xl font-bold text-white flex items-baseline gap-2">
            <Star className="w-6 h-6 fill-amber-400 text-amber-400 self-center" />
            {k.score.toFixed(1)}<span className="text-base font-medium text-slate-500">/ 5.0</span>
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {k.leaderboard
              ? t(`${PREFIX}.macro.leaderboard`, { positie: k.leaderboard.positie, deelnemers: k.leaderboard.deelnemers })
              : t(`${PREFIX}.macro.reviews`, { n: k.aantal })}
          </p>
        </>
      ) : (
        <>
          <p className="mt-2 text-3xl font-bold text-slate-600 flex items-center gap-2"><Lock className="w-6 h-6" />–</p>
          <p className="mt-1 text-xs text-slate-400">{t(`${PREFIX}.macro.scoreVergrendeld`, { n: k.aantal, drempel: k.drempel })}</p>
        </>
      )}
    </div>
  );
};

const MacroKaarten: React.FC<{ periode: Periode }> = ({ periode }) => {
  const { t } = useTranslation('pages');
  const { data, isLoading } = useQuery({ queryKey: ['opleider-macro', periode], queryFn: () => getMacro(periode) });
  if (isLoading || !data) return <Laad />;
  const { boekingsvolume: vol, vulgraad: vul } = data;
  const delta = vol.totaal - vol.vorige_periode;
  return (
    <section aria-labelledby="laag1" className="grid gap-4 md:grid-cols-3">
      <h2 id="laag1" className="sr-only">{t(`${PREFIX}.laag1`)}</h2>
      <div className={kaart}>
        <p className="text-sm text-slate-400 flex items-center gap-2"><Ticket className="w-4 h-4 text-blue-400" />{t(`${PREFIX}.macro.volume`)}</p>
        <p className="mt-2 text-3xl font-bold text-white">{getal.format(vol.totaal)}</p>
        <p className={`mt-1 text-xs inline-flex items-center gap-1 ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-rose-400' : 'text-slate-400'}`}>
          {delta > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : delta < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : null}
          {t(`${PREFIX}.macro.vorige`, { n: getal.format(vol.vorige_periode) })}
        </p>
        <ul className="mt-3 space-y-1 text-xs">
          {MARKTEN.map((m) => (
            <li key={m} className="flex items-center gap-2 text-slate-400">
              <MarktStip markt={m} /><span className="flex-1">{t(`opleiderCockpit.markt.${m}`)}</span>
              <span className="text-slate-200 tabular-nums">{getal.format(vol.per_markt[m])}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={kaart}>
        <p className="text-sm text-slate-400 flex items-center gap-2"><Gauge className="w-4 h-4 text-blue-400" />{t(`${PREFIX}.macro.vulgraad`)}</p>
        {vul.pct != null ? (
          <>
            <p className="mt-2 text-3xl font-bold text-white">{vul.pct.toLocaleString('nl-NL')}%</p>
            <div className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden" role="progressbar" aria-valuenow={vul.pct} aria-valuemin={0} aria-valuemax={100} aria-label={t(`${PREFIX}.macro.vulgraad`)}>
              <div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.min(100, vul.pct)}%` }} />
            </div>
            <p className="mt-2 text-xs text-slate-400">{t(`${PREFIX}.macro.stoelen`, { geboekt: vul.geboekt, capaciteit: vul.capaciteit, sessies: vul.sessies })}</p>
          </>
        ) : (
          <>
            <p className="mt-2 text-3xl font-bold text-slate-600">–</p>
            <p className="mt-1 text-xs text-slate-400">{t(`${PREFIX}.macro.geenKlassen`)}</p>
          </>
        )}
      </div>
      <KwaliteitsKaart k={data.kwaliteit} />
    </section>
  );
};

// ─── Laag 2: Segmentatie-Engine ──────────────────────────────────────────────

interface Tip { x: number; y: number; tekst: string }

/** Donut van de volumeverhouding; legenda met percentages = directe labels. */
const VolumeDonut: React.FC<{ data: Demografie }> = ({ data }) => {
  const { t } = useTranslation('pages');
  const [tip, setTip] = useState<Tip | null>(null);
  const totaal = data.volumes.reduce((a, b) => a + b, 0);
  const r = 40; const omtrek = 2 * Math.PI * r;
  // 2px oppervlak-gap tussen segmenten (alleen als er meer dan één segment is)
  const actief = data.volumes.filter((v) => v > 0).length;
  const gap = actief > 1 ? 1.2 : 0;
  let offset = 0;
  const pct = (v: number) => (totaal > 0 ? Math.round((v / totaal) * 100) : 0);
  return (
    <div className="relative flex flex-col items-center gap-5">
      <svg viewBox="0 0 100 100" className="w-40 h-40 -rotate-90 flex-shrink-0" role="img" aria-label={t(`${PREFIX}.seg.verhouding`)} onMouseLeave={() => setTip(null)}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="#1e293b" strokeWidth="14" />
        {totaal > 0 && MARKTEN.map((m, i) => {
          const deel = data.volumes[i] / totaal;
          const lengte = Math.max(0, deel * omtrek - gap);
          const el = deel > 0 ? (
            <circle
              key={m} cx="50" cy="50" r={r} fill="none" stroke={MARKT_KLEUR[m]} strokeWidth="14"
              strokeDasharray={`${lengte} ${omtrek}`} strokeDashoffset={-offset * omtrek}
              onMouseMove={(e) => {
                const box = (e.currentTarget.ownerSVGElement?.parentElement as HTMLElement).getBoundingClientRect();
                setTip({ x: e.clientX - box.left, y: e.clientY - box.top, tekst: `${t(`opleiderCockpit.markt.${m}`)}: ${getal.format(data.volumes[i])} (${pct(data.volumes[i])}%)` });
              }}
            />
          ) : null;
          offset += deel;
          return el;
        })}
      </svg>
      <ul className="text-sm space-y-2 w-full">
        {MARKTEN.map((m, i) => (
          <li key={m} className="flex items-center gap-2">
            <MarktStip markt={m} />
            <span className="text-slate-300 flex-1">{t(`opleiderCockpit.markt.${m}`)}</span>
            <span className="text-slate-400 tabular-nums">{getal.format(data.volumes[i])}</span>
            <span className="font-semibold text-white w-12 text-right tabular-nums">{pct(data.volumes[i])}%</span>
          </li>
        ))}
      </ul>
      {tip && <div className="pointer-events-none absolute z-10 rounded-md bg-slate-800 px-2 py-1 text-xs text-white shadow-lg" style={{ left: tip.x + 12, top: tip.y + 12 }}>{tip.tekst}</div>}
          </div>
  );
};

/**
 * Eén maat per grafiek (volume en omzet hebben elk hun eigen schaal — geen dubbele as).
 * Twee van deze naast elkaar vormen de Cross-Market Omzet-Vergelijker.
 */
const MarktStaven: React.FC<{ titel: string; waarden: number[]; format: (n: number) => string }> = ({ titel, waarden, format }) => {
  const { t } = useTranslation('pages');
  const max = Math.max(1, ...waarden);
  return (
    <figure>
      <figcaption className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-3">{titel}</figcaption>
      <div className="flex items-end gap-3 h-40 border-b border-slate-700">
        {MARKTEN.map((m, i) => (
          <div key={m} className="group relative flex-1 h-full flex flex-col justify-end items-center">
            <span className="mb-1 text-xs font-semibold text-slate-200 tabular-nums">{format(waarden[i])}</span>
            <div
              className="w-full max-w-[3.5rem] rounded-t transition-opacity group-hover:opacity-80"
              style={{ height: `${waarden[i] > 0 ? Math.max(2, (waarden[i] / max) * 80) : 0}%`, background: MARKT_KLEUR[m] }}
              title={`${t(`opleiderCockpit.markt.${m}`)}: ${format(waarden[i])}`}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-2">
        {MARKTEN.map((m) => <span key={m} className="flex-1 text-center text-[11px] text-slate-400 truncate">{t(`opleiderCockpit.markt.${m}`)}</span>)}
      </div>
    </figure>
  );
};

const Vergelijkingstabel: React.FC<{ data: Demografie }> = ({ data }) => {
  const { t } = useTranslation('pages');
  const leeg = '–';
  const rijen: Array<{ key: string; cel: (m: Markt) => React.ReactNode }> = [
    { key: 'volume', cel: (m) => getal.format(data.markten[m].volume) },
    { key: 'omzet', cel: (m) => eur.format(data.markten[m].omzet) },
    { key: 'leervorm', cel: (m) => (data.markten[m].primaire_leervorm ? t(`${PREFIX}.leervorm.${data.markten[m].primaire_leervorm}`) : leeg) },
    { key: 'boekingswaarde', cel: (m) => (data.markten[m].gem_boekingswaarde != null ? eur.format(data.markten[m].gem_boekingswaarde!) : leeg) },
    { key: 'prikkel', cel: (m) => <span className="text-slate-400">{t(`${PREFIX}.prikkel.${m}`)}</span> },
    {
      key: 'annulering',
      cel: (m) => (m === 'bedrijven' ? <span className="text-slate-400">{t(`${PREFIX}.tabel.vasteData`)}</span> : data.markten[m].annulering_pct != null ? `${data.markten[m].annulering_pct}%` : leeg),
    },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[40rem] text-sm">
        <caption className="sr-only">{t(`${PREFIX}.seg.tabel`)}</caption>
        <thead>
          <tr className="border-b border-slate-800">
            <th scope="col" className="py-2 pr-4 text-left font-medium text-slate-400">{t(`${PREFIX}.tabel.statistiek`)}</th>
            {MARKTEN.map((m) => (
              <th key={m} scope="col" className="py-2 px-3 text-left font-semibold text-slate-200">
                <span className="inline-flex items-center gap-2"><MarktStip markt={m} />{t(`opleiderCockpit.markt.${m}`)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rijen.map((r) => (
            <tr key={r.key}>
              <th scope="row" className="py-2.5 pr-4 text-left font-normal text-slate-400">{t(`${PREFIX}.tabel.${r.key}`)}</th>
              {MARKTEN.map((m) => <td key={m} className="py-2.5 px-3 text-slate-100 tabular-nums">{r.cel(m)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Segmentatie: React.FC<{ periode: Periode }> = ({ periode }) => {
  const { t } = useTranslation('pages');
  const { data, isLoading } = useQuery({ queryKey: ['opleider-demografie', periode], queryFn: () => getDemografie(periode) });
  if (isLoading || !data) return <Laad />;
  return (
    <section aria-labelledby="laag2" className="space-y-4">
      <h2 id="laag2" className="text-sm font-semibold uppercase tracking-wider text-slate-400">{t(`${PREFIX}.laag2`)}</h2>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className={kaart}>
          <h3 className="font-semibold text-white mb-4">{t(`${PREFIX}.seg.verhouding`)}</h3>
          <VolumeDonut data={data} />
        </div>
        <div className={kaart}>
          <h3 className="font-semibold text-white mb-1">{t(`${PREFIX}.seg.vergelijker`)}</h3>
          <p className="text-xs text-slate-400 mb-4">{t(`${PREFIX}.seg.vergelijkerUitleg`)}</p>
          <div className="grid grid-cols-2 gap-6">
            <MarktStaven titel={t(`${PREFIX}.seg.volume`)} waarden={data.volumes} format={(n) => getal.format(n)} />
            <MarktStaven titel={t(`${PREFIX}.seg.omzet`)} waarden={data.revenues} format={(n) => eur.format(n)} />
          </div>
        </div>
      </div>
      <div className={kaart}>
        <h3 className="font-semibold text-white mb-3">{t(`${PREFIX}.seg.tabel`)}</h3>
        <Vergelijkingstabel data={data} />
      </div>
    </section>
  );
};

const Analytics: React.FC = () => {
  const [periode, setPeriode] = useState<Periode>('maand');
  return (
    <div className="space-y-6">
      <div className="flex justify-end"><PeriodeKiezer waarde={periode} onChange={setPeriode} /></div>
      <MacroKaarten periode={periode} />
      <Segmentatie periode={periode} />
    </div>
  );
};

// ─── Laag 3: 3-Assen Review Model ────────────────────────────────────────────

/** Oplichtende voortgangsbalk op de schaal 1.0 – 5.0. */
const AsBalk: React.FC<{ label: string; vraag: string; waarde: number | null }> = ({ label, vraag, waarde }) => {
  const { t } = useTranslation('pages');
  const breedte = waarde != null ? ((Math.min(5, Math.max(1, waarde)) - 1) / 4) * 100 : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-slate-200 font-medium" title={vraag}>{label}</span>
        <span className="font-semibold text-white inline-flex items-center gap-1 tabular-nums">
          {waarde != null ? <><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />{waarde.toFixed(1)}</> : <span className="text-xs font-normal text-slate-500">{t(`${PREFIX}.assen.teWeinig`)}</span>}
        </span>
      </div>
      <div
        className="mt-1.5 h-2.5 rounded-full bg-slate-800 overflow-hidden"
        role="progressbar" aria-valuenow={waarde ?? undefined} aria-valuemin={1} aria-valuemax={5} aria-label={label}
      >
        {waarde != null && (
          <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.55)]" style={{ width: `${breedte}%` }} />
        )}
      </div>
    </div>
  );
};

const DrieAssen: React.FC<{ assen: Assen; variant: As2Variant | 'gemengd' }> = ({ assen, variant }) => {
  const { t } = useTranslation('pages');
  return (
    <div className="space-y-4">
      <AsBalk label={t(`${PREFIX}.assen.inhoud`)} vraag={t(`${PREFIX}.assen.inhoudVraag`)} waarde={assen.inhoud} />
      <AsBalk label={t(`${PREFIX}.assen.as2.${variant}`)} vraag={t(`${PREFIX}.assen.as2Vraag.${variant}`)} waarde={assen.impact} />
      <AsBalk label={t(`${PREFIX}.assen.structuur`)} vraag={t(`${PREFIX}.assen.structuurVraag`)} waarde={assen.structuur} />
      <div className="flex justify-between text-[10px] text-slate-500 tabular-nums" aria-hidden><span>1.0</span><span>2.0</span><span>3.0</span><span>4.0</span><span>5.0</span></div>
    </div>
  );
};

const Vergrendeld: React.FC<{ aantal: number; drempel: number }> = ({ aantal, drempel }) => {
  const { t } = useTranslation('pages');
  return (
    <div className="rounded-lg border border-dashed border-slate-700 bg-slate-900/40 p-4 text-sm text-slate-500">
      <p className="font-medium text-slate-400 inline-flex items-center gap-2"><Lock className="w-4 h-4" />{t(`${PREFIX}.slot.titel`)}</p>
      <p className="mt-1">{t(`${PREFIX}.slot.tekst`, { count: aantal, n: aantal, drempel })}</p>
    </div>
  );
};

const Reviews: React.FC = () => {
  const { t } = useTranslation('pages');
  const { data, isLoading } = useQuery({ queryKey: ['opleider-review-assen'], queryFn: getReviewAssen });
  if (isLoading || !data) return <Laad />;
  const variantenTotaal = new Set(data.trainingen.map((tr) => tr.as2_variant));
  const totaalVariant = variantenTotaal.size > 1 ? 'gemengd' : (data.trainingen[0]?.as2_variant ?? 'toepasbaarheid');
  return (
    <div className="space-y-6">
      <section aria-labelledby="laag3" className="grid gap-4 md:grid-cols-[16rem_minmax(0,1fr)]">
        <h2 id="laag3" className="sr-only">{t(`${PREFIX}.laag3`)}</h2>
        <KwaliteitsKaart k={data.platform} />
        <div className={kaart}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <h3 className="font-semibold text-white">{t(`${PREFIX}.assen.totaal`)}</h3>
            <span className="text-xs text-slate-400 flex-shrink-0">{t(`${PREFIX}.assen.aantal`, { n: data.totaal.aantal })}</span>
          </div>
          {data.totaal.assen ? <DrieAssen assen={data.totaal.assen} variant={totaalVariant} /> : <Vergrendeld aantal={data.totaal.aantal} drempel={data.drempel} />}
        </div>
      </section>
      {data.trainingen.length === 0 ? (
        <p className={`${kaart} text-center text-slate-400`}>{t(`${PREFIX}.assen.geen`)}</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.trainingen.map((tr) => (
            <article key={tr.id} className={`${kaart} ${tr.allow_display ? '' : 'opacity-80'}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <h3 className="font-semibold text-white truncate" title={tr.titel}>{tr.titel}</h3>
                <span className="text-xs text-slate-400 flex-shrink-0">{t(`${PREFIX}.assen.aantal`, { n: tr.aantal })}</span>
              </div>
              {tr.allow_display && tr.assen ? (
                <>
                  <DrieAssen assen={tr.assen} variant={tr.as2_variant} />
                  <p className="mt-4 text-xs text-slate-400 inline-flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" />{t(`${PREFIX}.assen.aanbevelers`, { n: tr.aanbevelers, totaal: tr.aantal })}</p>
                </>
              ) : <Vergrendeld aantal={tr.aantal} drempel={data.drempel} />}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Laag 4: AI-Sanitized Feedback Feed ──────────────────────────────────────

const Feedback: React.FC = () => {
  const { t } = useTranslation('pages');
  const { data, isLoading } = useQuery({ queryKey: ['opleider-feedback'], queryFn: getFeedback });
  if (isLoading || !data) return <Laad />;
  const vergrendeld = data.trainingen.filter((tr) => !tr.allow_display);
  const inScreening = data.trainingen.reduce((a, tr) => a + tr.in_screening, 0);
  return (
    <section aria-labelledby="laag4" className="space-y-5">
      <h2 id="laag4" className="sr-only">{t(`${PREFIX}.laag4`)}</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4 text-sm text-slate-300">
          <Lock className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-400" />
          <p><span className="font-semibold text-white">{t(`${PREFIX}.feed.grendel1`)}</span> {t(`${PREFIX}.feed.grendel1Tekst`, { n: data.drempel })}</p>
        </div>
        <div className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4 text-sm text-slate-300">
          <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5 text-fuchsia-400" />
          <p><span className="font-semibold text-white">{t(`${PREFIX}.feed.grendel2`)}</span> {t(`${PREFIX}.feed.grendel2Tekst`)}</p>
        </div>
      </div>

      {inScreening > 0 && (
        <p className="text-xs text-slate-400 inline-flex items-center gap-1.5"><Hourglass className="w-3.5 h-3.5" />{t(`${PREFIX}.feed.inScreening`, { count: inScreening, n: inScreening })}</p>
      )}

      {data.feed.length === 0 ? (
        <p className={`${kaart} text-center text-slate-400`}>{t(`${PREFIX}.feed.geen`)}</p>
      ) : (
        <ol className="space-y-3">
          {data.feed.map((f) => (
            <li key={f.id} className={kaart}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400 mb-2">
                <span className="font-medium text-slate-200 truncate max-w-full">{f.titel}</span>
                <span className="inline-flex items-center gap-1.5"><MarktStip markt={f.markt} />{t(`opleiderCockpit.markt.${f.markt}`)}</span>
                <span>{f.periode}</span>
              </div>
              <p className="text-sm text-slate-100 whitespace-pre-line">“{f.tekst}”</p>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                {f.aanbeveling != null && (
                  <span className="inline-flex items-center gap-1 text-amber-300"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />{t(`${PREFIX}.feed.aanbeveling`, { n: f.aanbeveling })}</span>
                )}
                <span className="inline-flex items-center gap-1 text-fuchsia-300">
                  <Sparkles className="w-3.5 h-3.5" />
                  {f.geschoond > 0 ? t(`${PREFIX}.feed.geschoond`, { count: f.geschoond, n: f.geschoond }) : t(`${PREFIX}.feed.schoon`)}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}

      {vergrendeld.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">{t(`${PREFIX}.feed.vergrendeld`)}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            {vergrendeld.map((tr) => (
              <div key={tr.id}>
                <p className="mb-1.5 text-sm font-medium text-slate-300 truncate" title={tr.titel}>{tr.titel}</p>
                <Vergrendeld aantal={tr.aantal} drempel={data.drempel} />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export const OpleiderDataPage: React.FC<{ view: DataView }> = ({ view }) => {
  const { t } = useTranslation('pages');
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="rounded-2xl bg-slate-950 text-slate-100 p-4 sm:p-6 shadow-xl ring-1 ring-slate-800">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400">{t(`${PREFIX}.kop`)}</p>
          <h1 className="mt-1 text-xl font-bold text-white">{t(`${PREFIX}.titel.${view}`)}</h1>
        </header>
        {view === 'analytics' && <Analytics />}
        {view === 'reviews' && <Reviews />}
        {view === 'feedback' && <Feedback />}
      </div>
    </div>
  );
};

export default OpleiderDataPage;
